<template>
  <div class="task-card-root d-flex flex-column h-100 overflow-hidden bg-surface">
    <!--
      1. Шапка: Заголовок задачи (с инлайн-редактированием), справа скрепка, бургер и кнопки модалки (слот header-actions).
      На xs иконки уходят отдельной строкой над заголовком, как в форме НС.
    -->
    <header
      class="task-card-header flex-shrink-0 pb-2 d-flex align-center justify-space-between"
      :class="xs ? 'px-4 pt-3 flex-wrap' : 'px-6 pt-6'"
    >
      <!-- Заголовок задачи -->
      <div class="d-flex align-center ga-2 flex-grow-1 mr-3 overflow-hidden">
        <VTooltip v-if="taskData.is_high_priority" location="top" text="Срочная задача">
          <template #activator="{ props: tipProps }">
            <VIcon
              v-bind="tipProps"
              icon="ri-fire-fill"
              color="warning"
              size="22"
              class="flex-shrink-0"
            />
          </template>
        </VTooltip>

        <div v-if="isEditingTitle" class="flex-grow-1">
          <VTextField
            v-model="titleInput"
            density="compact"
            variant="underlined"
            autofocus
            hide-details
            class="task-title-inline-input"
            @keydown.enter="saveTitle"
            @keydown.esc="isEditingTitle = false"
            @blur="saveTitle"
          />
        </div>

        <VTooltip v-else :disabled="!canEditTitle" location="top" text="Нажмите для редактирования">
          <template #activator="{ props: tipProps }">
            <h2
              v-bind="tipProps"
              class="task-card-title text-high-emphasis mb-0"
              :class="{ 'cursor-pointer hover-editable-text': canEditTitle, 'text-truncate': !xs }"
              @click="canEditTitle ? startEditTitle() : undefined"
            >
              {{ taskData.title || taskData.task || taskData.instructions || 'Задача' }}
            </h2>
          </template>
        </VTooltip>
      </div>

      <!-- Иконки справа: Скрепка и Бургер-меню (строго по макетам) -->
      <div class="d-flex align-center ga-1 flex-shrink-0" :class="{ 'task-card-header-actions--xs': xs }">
        <VTooltip location="top" text="Прикрепить файл">
          <template #activator="{ props: tooltipProps }">
            <VBtn
              v-bind="tooltipProps"
              variant="text"
              icon="ri-attachment-line"
              size="small"
              color="secondary"
              @click="triggerTaskFileInput"
            />
          </template>
        </VTooltip>

        <VMenu location="bottom end" content-class="entity-form-menu">
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              :data-onboarding="SHARED_ANCHORS.taskCard.menu"
              variant="text"
              icon
              density="compact"
              size="small"
              color="secondary"
              class="flex-shrink-0"
              @click.stop
            >
              <VIcon icon="ri-menu-line" size="18" />
            </VBtn>
          </template>

          <VList density="compact" class="py-1 task-burger-menu">
            <!-- Отметить как срочную / Снять отметку срочной -->
            <VListItem v-if="canTogglePriority" class="cursor-pointer task-burger-menu-item" @click="togglePriority">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-fire-line" size="20" class="flex-shrink-0" />
                <span class="task-burger-menu-text">
                  {{ taskData.is_high_priority ? 'Убрать отметку срочности' : 'Отметить как срочную' }}
                </span>
              </div>
            </VListItem>

            <!-- Закрепить / Открепить -->
            <VListItem class="cursor-pointer task-burger-menu-item" @click="togglePin">
              <div class="d-flex align-center ga-2">
                <VIcon :icon="taskData.is_pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'" size="20" class="flex-shrink-0" />
                <span class="task-burger-menu-text">
                  {{ taskData.is_pinned ? 'Открепить' : 'Закрепить' }}
                </span>
              </div>
            </VListItem>

            <!-- Копировать ссылку на задачу (только для основных задач) -->
            <VListItem v-if="!hasParentTask" class="cursor-pointer task-burger-menu-item" @click="copyLink">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-file-copy-line" size="20" class="flex-shrink-0" />
                <span class="task-burger-menu-text">Копировать ссылку на задачу</span>
              </div>
            </VListItem>

            <!-- Удалить -->
            <VListItem
              v-if="canDeleteTask"
              class="cursor-pointer task-burger-menu-item"
              @click="isDeleteDialogOpen = true"
            >
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-delete-bin-line" size="20" class="flex-shrink-0" />
                <span class="task-burger-menu-text">
                  {{ hasParentTask ? 'Удалить подзадачу' : 'Удалить задачу' }}
                </span>
              </div>
            </VListItem>
          </VList>
        </VMenu>

        <slot name="header-actions" />
      </div>
    </header>

    <!-- 2. Скроллируемая область контента карточки задачи -->
    <div class="task-card-body flex-grow-1 overflow-y-auto py-2" :class="xs ? 'px-4' : 'px-6'" style="min-height: 0;">

    <!-- 3. Поле «Описание» СРАЗУ ПОД ЗАГОЛОВКОМ (Скриншот 7 дефолт, Скриншот 8 редактор) -->
    <div class="mb-4">
      <!-- 3. Описание задачи через переиспользуемый компонент (Скриншот 7 и 8) -->
      <TaskDescriptionField
        :model-value="taskData.instructions"
        :can-edit="canEditDescription"
        @save="saveDescription"
      />

      <!-- Список прикрепленных файлов задачи через системный FileList -->
      <div v-if="taskFiles.length > 0" class="mt-2">
        <FileList :items="taskFiles" :show-delete="false" />
      </div>

      <!-- Прикрепление новых файлов к задаче через системный FileAttach -->
      <div v-show="taskNewFiles.length > 0" class="mt-2">
        <FileAttach
          ref="taskFileAttachRef"
          :files="taskNewFiles"
          accept="*"
          @update:files="handleNewFilesUploaded"
        />
      </div>
    </div>

    <!-- 4. Чек-листы СРАЗУ ПОД ОПИСАНИЕМ (Card_чек-лист свернут, Card_чек-лист создан) -->
    <div v-if="checklists.length > 0" class="mb-4" :data-onboarding="SHARED_ANCHORS.taskCard.checklist">
      <TaskChecklist
        v-for="cl in checklists"
        :key="cl.id"
        :entry-id="taskData.id!"
        :checklist="cl"
        :can-edit="canEditChecklistStructure"
        :can-toggle="canToggleChecklist"
        :initial-collapsed="!newlyCreatedChecklistIds.has(cl.id)"
        :error="showChecklistError"
        @deleted="handleChecklistDeleted"
      />
    </div>

    <!-- 5. Чистый список метаданных (БЕЗ рамок и карточек, строго по макетам!) -->
    <div class="task-metadata-list d-flex flex-column ga-4 mb-5">
      <!-- Постановщик -->
      <div class="d-flex align-center" style="min-height: 36px;">
        <span class="metadata-label">Постановщик</span>
        <div class="metadata-value d-flex align-center ga-2">
          <template v-if="taskData.supervisor">
            <VAvatar size="32" class="border flex-shrink-0">
              <VImg :src="getEmployeeAvatar(taskData.supervisor, taskData.supervisor?.id || 'supervisor')" />
            </VAvatar>
            <div class="d-flex flex-column">
              <span class="text-body-2 font-weight-medium text-high-emphasis">{{ supervisorName }}</span>
              <span v-if="taskData.supervisor.position_name" class="text-caption text-medium-emphasis">
                {{ taskData.supervisor.position_name }}
              </span>
            </div>
          </template>
          <span v-else-if="isAutoCreated" class="text-body-2 auto-created-text">Создана автоматически</span>
          <span v-else class="text-body-2 text-medium-emphasis">—</span>
        </div>
      </div>

      <!-- Исполнитель -->
      <div class="d-flex align-center" style="min-height: 36px;" :data-onboarding="SHARED_ANCHORS.taskCard.performer">
        <span class="metadata-label">
          {{ performerLabel }}
        </span>
        <div
          class="metadata-value d-flex align-center ga-2"
          :class="{ 'cursor-pointer hover-bg-subtle pa-1 rounded': canChangePerformer }"
          @click="canChangePerformer ? openChangePerformerModal() : undefined"
        >
          <template v-if="taskData.responsible_employee">
            <VAvatar size="32" class="border flex-shrink-0">
              <VImg :src="getEmployeeAvatar(taskData.responsible_employee, taskData.responsible_employee?.id || 'performer')" />
            </VAvatar>
            <div class="d-flex flex-column">
              <span class="text-body-2 font-weight-medium text-high-emphasis">{{ performerName }}</span>
              <span v-if="taskData.responsible_employee.position_name" class="text-caption text-medium-emphasis">
                {{ taskData.responsible_employee.position_name }}
              </span>
            </div>
            <!-- Явный признак, что исполнителя можно сменить (клик по всему блоку работает как раньше) -->
            <VIcon v-if="canChangePerformer" icon="ri-edit-line" size="16" class="text-medium-emphasis ms-1 flex-shrink-0" />
          </template>
          <span v-else class="text-body-2 text-medium-emphasis">—</span>
        </div>
      </div>

      <!-- Крайний срок -->
      <div class="d-flex align-center" style="min-height: 36px;" :data-onboarding="SHARED_ANCHORS.taskCard.deadline">
        <span class="metadata-label">Крайний срок</span>
        <div class="metadata-value d-flex align-center ga-2">
          <!-- Редактируемый дедлайн при наличии прав editable_fields 'deadline_time' -->
          <VMenu
            v-if="canEditDeadline"
            v-model="isDeadlinePickerOpen"
            :close-on-content-click="false"
            location="bottom start"
          >
            <template #activator="{ props: menuProps }">
              <div
                v-bind="menuProps"
                class="d-flex align-center ga-2 cursor-pointer hover-bg-subtle pa-1 rounded"
              >
                <VIcon icon="ri-calendar-line" size="18" color="primary" />
                <span class="text-body-2 font-weight-medium text-high-emphasis">
                  {{ deadlineFormatted || 'Без срока' }}
                </span>
                <VTooltip location="top" text="Изменить крайний срок">
                  <template #activator="{ props: tipProps }">
                    <VIcon
                      v-bind="tipProps"
                      icon="ri-edit-line"
                      size="14"
                      class="text-medium-emphasis ms-1"
                    />
                  </template>
                </VTooltip>
                <VTooltip v-if="taskData.deadline_time" location="top" text="Удалить крайний срок">
                  <template #activator="{ props: tipProps }">
                    <VBtn
                      v-bind="tipProps"
                      icon="ri-delete-bin-line"
                      size="x-small"
                      variant="text"
                      color="error"
                      density="compact"
                      class="ms-1"
                      @click.stop="handleClearDeadline"
                    />
                  </template>
                </VTooltip>
              </div>
            </template>
            <VCard min-width="320">
              <DateTimePicker
                :model-value="deadlineDateValue"
                @save="handleSaveDeadline"
              />
            </VCard>
          </VMenu>

          <!-- Статичный дедлайн если нет прав -->
          <template v-else>
            <VIcon icon="ri-calendar-line" size="18" color="primary" />
            <span class="text-body-2 font-weight-medium text-high-emphasis">
              {{ deadlineFormatted || 'Без срока' }}
            </span>
          </template>

          <VChip
            v-if="isOverdue && !isCompleted"
            size="x-small"
            variant="outlined"
            color="error"
            class="font-weight-medium ms-1"
          >
            {{ overdueBadgeText }}
          </VChip>
        </div>
      </div>

      <!-- Статус -->
      <div class="d-flex align-center" style="min-height: 36px;">
        <span class="metadata-label">Статус</span>
        <div class="metadata-value d-flex align-center ga-2">
          <VChip
            size="small"
            class="status-badge font-weight-medium"
            :class="taskData.status"
          >
            {{ statusDisplayText }}
          </VChip>
        </div>
      </div>

      <!-- Наблюдатели (Card_наблюдатели: отображаются только когда назначены) -->
      <div v-if="taskObservers.length > 0" class="d-flex align-center" style="min-height: 36px;" :data-onboarding="SHARED_ANCHORS.taskCard.observers">
        <span class="metadata-label">Наблюдатели</span>
        <div class="metadata-value d-flex align-center ga-2">
          <TaskObserversPopover
            :entry-id="taskData.id!"
            :observers="taskObservers"
            :can-manage="canManageObservers"
            @updated="handleObserversUpdated"
          />
        </div>
      </div>
    </div>

    <!-- 6. Кнопки действий второго уровня в ряд: [+ Подзадача] [👁 Наблюдатели] [☑ Чек-лист] -->
    <div class="d-flex ga-2 mb-4" :class="xs ? 'flex-column' : 'align-center flex-wrap'">
      <VBtn
        v-if="!hasParentTask && canCreateSubtask"
        variant="outlined"
        color="secondary"
        class="text-none"
        prepend-icon="ri-add-line"
        :data-onboarding="SHARED_ANCHORS.taskCard.subtask"
        @click="isCreateSubtaskModalOpen = true"
      >
        Подзадача
      </VBtn>

      <VBtn
        v-if="canManageObservers"
        variant="outlined"
        color="secondary"
        class="text-none"
        prepend-icon="ri-eye-line"
        :data-onboarding="SHARED_ANCHORS.taskCard.observers"
        @click="openAddObserversModal"
      >
        Наблюдатели
      </VBtn>

      <VBtn
        v-if="canEditChecklistStructure"
        variant="outlined"
        color="secondary"
        class="text-none"
        prepend-icon="ri-checkbox-line"
        :disabled="checklists.length > 0"
        :data-onboarding="SHARED_ANCHORS.taskCard.checklist"
        @click="addChecklist"
      >
        Чек-лист
      </VBtn>
    </div>

    <!-- 7. Блок «Родительская задача» (если открыта подзадача) строго под кнопками, как на скриншоте -->
    <div
      v-if="hasParentTask"
      class="border rounded-lg overflow-hidden bg-surface mb-6 flex-shrink-0"
    >
      <!-- Шапка родительской задачи со светло-серым фоном -->
      <div class="d-flex align-center ga-2 px-4 py-2 border-b task-section-header flex-shrink-0">
        <VIcon icon="ri-git-merge-line" size="18" color="primary" />
        <span class="text-body-1 font-weight-medium text-high-emphasis">
          Родительская задача
        </span>
      </div>

      <!-- Строка родительской задачи -->
      <div
        class="d-flex align-center justify-space-between px-4 py-3 cursor-pointer select-none hover-bg-subtle flex-shrink-0"
        @click="navigateToParentTask"
      >
        <span class="text-body-1 font-weight-medium text-high-emphasis text-truncate mr-4">
          {{ parentTaskTitle }}
        </span>

        <div class="d-flex align-center ga-3 flex-shrink-0">
          <VAvatar size="24" class="border flex-shrink-0">
            <VImg :src="getEmployeeAvatar(parentResponsible, parentResponsible?.id || 'parent')" />
          </VAvatar>

          <div v-if="parentDeadlineFormatted" class="border-s flex-shrink-0" style="height: 16px;" />

          <div v-if="parentDeadlineFormatted" class="d-flex align-center ga-1 flex-shrink-0">
            <VIcon icon="ri-calendar-line" size="16" class="text-medium-emphasis flex-shrink-0" />
            <span class="text-body-1 font-weight-medium text-high-emphasis">
              {{ parentDeadlineFormatted }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Блок подзадач (Скриншоты 17, 18) — только для родительской задачи, строго под кнопками -->
    <div
      v-else-if="subtasksList.length > 0"
      class="border rounded-lg overflow-hidden bg-surface mb-6 flex-shrink-0"
    >
      <!-- Шапка подзадач со светло-серым фоном -->
      <div class="d-flex align-center ga-2 px-4 py-2 border-b task-section-header flex-shrink-0">
        <VIcon icon="ri-git-merge-line" size="18" color="primary" />
        <span class="text-body-1 font-weight-medium text-high-emphasis">
          Подзадачи: {{ subtasksList.length }}
        </span>
      </div>

      <!-- Список строк подзадач -->
      <div class="subtasks-list d-flex flex-column">
        <TaskSubtaskCard
          v-for="(st, idx) in subtasksList"
          :key="st.id"
          :subtask="st"
          :index="idx"
          :is-last="idx === subtasksList.length - 1"
          @open="handleOpenSubtask"
        />
      </div>
    </div>

    <!-- Блок написания отчета (по макету Card_подзадача_написание отчета_исполнитель) -->
    <div v-if="isWritingReport" class="mb-6 flex-shrink-0">
      <div class="d-flex align-start ga-2 mb-3">
        <VTextarea
          v-model="completeReportText"
          label="Отчет"
          placeholder="Опишите выполненные действия"
          persistent-placeholder
          rows="3"
          variant="outlined"
          color="primary"
          class="flex-grow-1"
          hide-details="auto"
          auto-grow
        />
        <VTooltip location="top" text="Прикрепить файлы">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              icon="ri-attachment-line"
              variant="text"
              size="small"
              color="secondary"
              class="mt-2 flex-shrink-0"
              @click="completeFileAttachRef?.openDialog()"
            />
          </template>
        </VTooltip>
      </div>

      <!-- Прикрепление и список файлов отчета через системный FileAttach -->
      <FileAttach
        ref="completeFileAttachRef"
        v-model:files="completeFiles"
      />
    </div>

    <!-- Блок просмотра отправленного отчета (по макету Card_подзадача_отчет отправлен_исполнитель) -->
    <div v-else-if="isCompleted && (completedReportText || completedReportFiles.length > 0)" class="mb-6 flex-shrink-0">
      <div class="d-flex align-start ga-2 mb-3">
        <VTextarea
          :model-value="completedReportText"
          label="Отчет"
          readonly
          rows="3"
          variant="outlined"
          class="flex-grow-1"
          hide-details
        />
        <VIcon
          icon="ri-attachment-line"
          size="20"
          color="secondary"
          class="mt-3 flex-shrink-0 opacity-60"
        />
      </div>

      <!-- Список существующих файлов завершенного отчета через системный FileList -->
      <FileList
        v-if="completedReportFiles.length > 0"
        :items="completedReportFiles"
        :disabled="true"
        :show-delete="false"
      />
    </div>

    </div>

    <!-- 3. Фиксированный футер (системный паттерн с padding px-6 py-4, на xs — px-4 py-3) -->
    <footer
      class="task-card-footer flex-shrink-0 border-t d-flex align-center justify-space-between flex-wrap ga-2"
      :class="xs ? 'px-4 py-3' : 'px-6 py-4'"
    >
      <!-- На узком экране части переносятся целиком, а не ломаются посреди даты/ID -->
      <div class="d-flex align-center flex-wrap ga-1 text-caption text-medium-emphasis">
        <VIcon icon="ri-time-line" size="14" class="mr-1" />
        <span class="text-no-wrap">Дата создания: {{ createdAtFormatted }}</span>
        <span class="mx-1">/</span>
        <span class="text-no-wrap">ID {{ taskData.id }}</span>
        <VTooltip v-if="!xs" location="top" text="Копировать ID">
          <template #activator="{ props: tipProps }">
            <VBtn
              v-bind="tipProps"
              variant="text"
              icon="ri-file-copy-line"
              size="x-small"
              color="secondary"
              @click="copyTaskId"
            />
          </template>
        </VTooltip>
      </div>

      <!-- Кнопки действий жизненного цикла (БЕЗ иконок по макету); на xs — колонкой на всю ширину -->
      <div class="d-flex ga-2" :class="xs ? 'flex-column w-100' : 'align-center'" :data-onboarding="SHARED_ANCHORS.taskCard.statusActions">
        <!-- Режим написания отчета: Отменить / Сохранить -->
        <template v-if="isWritingReport">
          <VBtn
            variant="outlined"
            color="secondary"
            class="text-none px-6 font-weight-medium rounded-lg"
            height="38"
            @click="cancelWritingReport"
          >
            Отменить
          </VBtn>

          <VBtn
            color="primary"
            class="text-none px-6 font-weight-medium rounded-lg"
            height="38"
            :loading="isActionLoading"
            :disabled="isReportRequired && !completeReportText.trim()"
            @click="submitComplete"
          >
            Сохранить
          </VBtn>
        </template>

        <!-- Обычный режим: Взять в работу / Отчитаться / Завершить / Вернуть в работу -->
        <template v-else>
          <VBtn
            v-if="canReturnInProgress"
            color="primary"
            class="text-none px-6 font-weight-medium rounded-lg"
            height="38"
            :loading="isActionLoading"
            @click="returnInProgress"
          >
            Вернуть в работу
          </VBtn>

          <VBtn
            v-if="canTakeInProgress"
            color="primary"
            class="text-none px-6 font-weight-medium rounded-lg"
            height="38"
            :loading="isActionLoading"
            @click="takeInProgress"
          >
            Взять в работу
          </VBtn>

          <VBtn
            v-if="canCompleteTask"
            color="primary"
            class="text-none px-6 font-weight-medium rounded-lg"
            height="38"
            :loading="isActionLoading"
            @click="handleCompleteActionClick"
          >
            {{ isReportRequired ? 'Отчитаться' : 'Завершить' }}
          </VBtn>
        </template>
      </div>
    </footer>

    <!-- Модалка создания подзадачи на базе универсального CreateTaskModal -->
    <CreateTaskModal
      v-if="isCreateSubtaskModalOpen"
      v-model="isCreateSubtaskModalOpen"
      :parent-task-id="taskData.id"
      @created="handleSubtaskCreated"
    />

    <!-- Диалог подтверждения удаления -->
    <ConfirmDialog
      :is-dialog-visible="isDeleteDialogOpen"
      :confirmation-question="hasParentTask ? 'Вы действительно хотите удалить эту подзадачу?' : 'Вы действительно хотите удалить эту задачу?'"
      :confirm-title="hasParentTask ? 'Удалить подзадачу' : 'Удалить задачу'"
      confirm-btn="Удалить"
      reject-btn="Отмена"
      @confirm="handleDeleteConfirm"
      @update:is-dialog-visible="isDeleteDialogOpen = $event"
    />

    <!-- Диалог подтверждения отмены несохраненного отчета -->
    <ConfirmDialog
      :is-dialog-visible="showUnsavedReportDialog"
      confirmation-question="Закрыть без сохранения?"
      confirmation-text="Введенный отчет не сохранится"
      confirm-btn="Закрыть"
      reject-btn="Отменить"
      @confirm="handleCancelReportConfirm"
      @update:is-dialog-visible="showUnsavedReportDialog = $event"
    />

    <!-- Диалог подтверждения завершения с невыполненными пунктами чек-листа или подзадачами -->
    <ConfirmDialog
      :is-dialog-visible="showIncompleteItemsDialog"
      confirmation-question="Завершить задачу?"
      :confirmation-text="incompleteDialogText"
      confirm-btn="Завершить"
      reject-btn="Отмена"
      @confirm="handleIncompleteItemsConfirm"
      @update:is-dialog-visible="showIncompleteItemsDialog = $event"
    />

    <!-- Модалка смены исполнителя -->
    <EmployeeSelectionModal
      ref="employeeModalRef"
      :employees="apiStore.employees"
      :multiple="false"
      title="Назначить исполнителя"
      apply-label="Назначить"
      @select="handlePerformerSelected"
    />

    <!-- Модалка добавления наблюдателей -->
    <EmployeeSelectionModal
      ref="observersModalRef"
      :employees="apiStore.employees"
      :multiple="true"
      title="Добавить наблюдателей"
      apply-label="Добавить"
      @select-multiple="handleObserversSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import DateTimePicker from '@/components/createdUi/DateTimePicker.vue'
import FileList from '@/modules/files/components/FileList.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { openEntityByUrlProps } from '@/modules/notifications/utils/notificationNavigation'
import CreateTaskModal from '@/modules/tasks/components/CreateTaskModal.vue'
import TaskChecklist from '@/modules/tasks/components/TaskChecklist.vue'
import TaskDescriptionField from '@/modules/tasks/components/TaskDescriptionField.vue'
import TaskObserversPopover from '@/modules/tasks/components/TaskObserversPopover.vue'
import TaskSubtaskCard from '@/modules/tasks/components/TaskSubtaskCard.vue'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import type { ITaskChecklist } from '@/modules/tasks/types/ITaskChecklist'
import type { ITaskObserver } from '@/modules/tasks/types/ITaskObserver'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
import { useTicketsStore } from '@/modules/tickets/stores/ticketsStore'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import { useCitizenInquiriesStore } from '@/modules/citizenInquiries/stores/citizenInquiriesStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { formatDateTime, getFullName, getPersonDisplayName, parseFullName, formatResponsibilityDate } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { SHARED_ANCHORS } from '@/modules/onboarding/scenarios/shared/anchors'

interface Props {
  task: IResponsibilityListItem
  isEmbedded?: boolean

  /** Учебная задача онбординга: id фиктивный, данные уже в `task`, на бек не ходим */
  isDemo?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEmbedded: false,
  isDemo: false,
})

const emit = defineEmits<{
  (e: 'navigate-task', taskId: number): void
  (e: 'deleted', taskId: number): void
  (e: 'updated', task: IResponsibilityListItem): void
}>()

const router = useRouter()
const { xs } = useDisplay()
const apiStore = useApiStore()
const userStore = useUserStore()
const tasksStore = useTasksStore()
const ticketsStore = useTicketsStore()
const improvementsStore = useImprovementsStore()
const citizenInquiriesStore = useCitizenInquiriesStore()
const feedbackStore = useFeedbackStore()

const taskData = ref<IResponsibilityListItem>({ ...props.task })
const checklists = ref<ITaskChecklist[]>([])
const newlyCreatedChecklistIds = ref<Set<number>>(new Set())
const taskObservers = ref<ITaskObserver[]>([])
const subtasksList = ref<IResponsibilityListItem[]>([])

// Файлы задачи и отчета
const taskFiles = computed<IExistingFile[]>(() => {
  return (taskData.value.files || []) as IExistingFile[]
})
const taskNewFiles = ref<File[]>([])
const taskFileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)

const completeFiles = ref<File[]>([])
const completeFileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)

// Инлайн-редактирование
const isEditingTitle = ref(false)
const isSavingTitle = ref(false)
const titleInput = ref('')

// Диалоги и формы
const isCreateSubtaskModalOpen = ref(false)
const isWritingReport = ref(false)
const completeReportText = ref('')
const isActionLoading = ref(false)
const isDeleteDialogOpen = ref(false)
const isPerformerModalOpen = ref(false)
const reportFileInputRef = ref<HTMLInputElement | null>(null)

// Проверка наличия родительской задачи
const parentEntryData = computed(() => {
  return taskData.value.parent_entry
})

const hasParentTask = computed(() => {
  return Boolean(parentEntryData.value)
})

const parentEntryDetail = ref<IResponsibilityListItem | null>(null)

async function loadParentEntryDetail() {
  const p = parentEntryData.value
  const id = typeof p === 'object' && p ? p.id : Number(p)
  if (!id) {
    parentEntryDetail.value = null
    return
  }
  if (typeof p === 'object' && p && (p.title || (p as any).instructions) && p.responsible_employee) {
    parentEntryDetail.value = p as IResponsibilityListItem
    return
  }
  try {
    const detail = await tasksStore.fetchEntryById(id)
    if (detail) {
      parentEntryDetail.value = detail
    }
  } catch {
    // Игнорируем сетевые ошибки фоновой подгрузки
  }
}

watch(
  () => parentEntryData.value,
  () => {
    void loadParentEntryDetail()
  },
  { immediate: true },
)

const parentTaskTitle = computed(() => {
  if (parentEntryDetail.value) {
    return parentEntryDetail.value.title || parentEntryDetail.value.instructions || `Задача #${parentEntryDetail.value.number || parentEntryDetail.value.id}`
  }
  const p = parentEntryData.value
  if (!p) return ''
  if (typeof p === 'object') {
    return p.title || (p as any).instructions || `Задача #${p.number || p.id}`
  }
  return `Задача #${p}`
})

const parentResponsible = computed(() => {
  if (parentEntryDetail.value?.responsible_employee) {
    return parentEntryDetail.value.responsible_employee
  }
  const p = parentEntryData.value
  if (typeof p === 'object' && p) {
    return p.responsible_employee
  }
  return null
})

const parentInitials = computed(() => {
  const emp = parentResponsible.value
  if (!emp) return '?'
  const last = emp.last_name?.[0] || ''
  const first = emp.first_name?.[0] || ''
  return `${last}${first}`.toUpperCase()
})

const parentDeadlineFormatted = computed(() => {
  const deadline = parentEntryDetail.value?.deadline_time || (typeof parentEntryData.value === 'object' ? parentEntryData.value?.deadline_time : null)
  if (deadline) {
    const d = new Date(deadline)
    if (isNaN(d.getTime())) return formatResponsibilityDate(deadline)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day}.${month}.${year}, ${hours}:${minutes}`
  }
  return null
})

// Координаторскую задачу без постановщика создаёт бэк автоматически (вручную is_coordinator_entry не ставится)
const isAutoCreated = computed(() => Boolean(taskData.value.is_coordinator_entry && !taskData.value.supervisor))

const supervisorName = computed(() => {
  return taskData.value.supervisor ? getPersonDisplayName(taskData.value.supervisor) : ''
})

const performerName = computed(() => {
  return taskData.value.responsible_employee ? getPersonDisplayName(taskData.value.responsible_employee) : ''
})

const deadlineFormatted = computed(() => {
  return formatDateTime(taskData.value.deadline_time)
})

const isDeadlinePickerOpen = ref(false)

const deadlineDateValue = computed<Date | null>(() => {
  if (!taskData.value.deadline_time) return null
  const d = new Date(taskData.value.deadline_time)
  return isNaN(d.getTime()) ? null : d
})

async function handleSaveDeadline(val: Date | null) {
  isDeadlinePickerOpen.value = false
  if (!taskData.value.id) return
  const newIso = val ? val.toISOString() : null
  if (newIso === taskData.value.deadline_time) return

  const prev = taskData.value.deadline_time
  taskData.value.deadline_time = newIso
  try {
    const updated = await tasksStore.updateTaskDetails(taskData.value.id, { deadline_time: newIso })
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      emit('updated', taskData.value)
    }
  } catch {
    taskData.value.deadline_time = prev
  }
}

async function handleClearDeadline() {
  await handleSaveDeadline(null)
}

const createdAtFormatted = computed(() => {
  return formatDateTime(taskData.value.created_at)
})

const isCompleted = computed(() => {
  return taskData.value.status === 'completed'
})

const isOverdue = computed(() => {
  return Boolean(taskData.value.is_overdue)
})

const overdueBadgeText = computed(() => {
  if (!taskData.value.deadline_time) return 'Просрочено'
  const diff = Date.now() - new Date(taskData.value.deadline_time).getTime()
  if (diff <= 0) return 'Просрочено'
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  return days > 0 ? `Просрочено на ${days} д.` : 'Просрочено'
})

const statusDisplayText = computed(() => {
  const status = taskData.value.status
  if (!status) return 'Ожидает'
  const statuses = apiStore.responsibilityEntriesStatuses
  return (statuses as Record<string, string>)[status] || taskData.value.status_label || status
})

// У ППУ координатор — председатель комиссии (бэк создаёт задачу на него), так его и подписываем
const performerLabel = computed(() => {
  if (!isCoordinatorRole.value)
    return 'Исполнитель'
  return taskData.value.source_type === 'improvementsuggestion' ? 'Председатель\nкомиссии' : 'Исполнитель -\nкоординатор'
})

const isCoordinatorRole = computed(() => {
  return Boolean(taskData.value.is_coordinator_entry)
})

const userActions = computed<string[]>(() => {
  return (taskData.value.actions || []) as string[]
})

const editableFields = computed<string[]>(() => {
  return (taskData.value.editable_fields || []) as string[]
})

// Базовые действия строго из actions бэкенда
const canManageTask = computed(() => {
  return userActions.value.includes('manage')
})

const canTakeInProgress = computed(() => {
  return userActions.value.includes('in-progress')
})

const canCompleteTask = computed(() => {
  return userActions.value.includes('complete')
})

const canReturnInProgress = computed(() => {
  return userActions.value.includes('return')
})

const canCreateSubtask = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('subtasks')
  }
  return userActions.value.includes('subtask') || canManageTask.value
})

// Точечные разрешения на редактирование конкретных полей из editable_fields бэкенда
const canEditTitle = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('title')
  }
  return canManageTask.value
})

const canEditDescription = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('instructions')
  }
  return canManageTask.value
})

const canChangePerformer = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('responsible_employee')
  }
  return canManageTask.value
})

const canEditDeadline = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('deadline_time')
  }
  return canManageTask.value
})

const canTogglePriority = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('is_high_priority')
  }
  return canManageTask.value
})

const canDeleteTask = computed(() => {
  return canManageTask.value && !taskData.value.is_coordinator_entry
})

const canManageObservers = computed(() => {
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('observers')
  }
  return canManageTask.value
})

const isPerformer = computed(() => {
  const currentEmpId = userStore.user?.id
  const taskPerformerId = taskData.value.responsible_employee?.id
  if (currentEmpId && taskPerformerId && String(currentEmpId) === String(taskPerformerId)) return true
  if (userActions.value.includes('in-progress') || userActions.value.includes('complete')) return true
  return false
})

const canToggleChecklist = computed(() => {
  if (isCompleted.value) return false
  return isPerformer.value || canEditChecklistStructure.value
})

const canEditChecklistStructure = computed(() => {
  if (isCompleted.value) return false
  if (editableFields.value.length > 0) {
    return editableFields.value.includes('checklists')
  }
  return canManageTask.value
})

const canEditContent = computed(() => {
  return canManageTask.value || editableFields.value.length > 0
})

// При смене ID задачи перезагружаем детали
watch(
  () => props.task?.id,
  (newId, oldId) => {
    taskData.value = { ...props.task }
    newlyCreatedChecklistIds.value.clear()
    if (newId && newId !== oldId) {
      loadTaskDetails()
    }
  },
)

// При обновлении полей той же задачи синхронизируем данные без лишних сетевых запросов
watch(
  () => props.task,
  newTask => {
    if (newTask && newTask.id === taskData.value.id) {
      taskData.value = { ...newTask }
      if (!isEditingTitle.value) {
        titleInput.value = newTask.title || newTask.instructions || ''
      }
      if (Array.isArray(newTask.checklists)) {
        checklists.value = [...newTask.checklists]
      }
      if (Array.isArray(newTask.observers)) {
        taskObservers.value = [...newTask.observers]
      }
      if (Array.isArray(newTask.subtasks)) {
        subtasksList.value = [...newTask.subtasks]
      }
    }
  },
  { deep: true },
)

// Синхронизация с деталями из tasksStore при их фоновом обновлении через Centrifugo
watch(
  () => tasksStore.currentEntry,
  newEntry => {
    if (newEntry && newEntry.id === taskData.value.id) {
      taskData.value = { ...taskData.value, ...newEntry }
      if (Array.isArray(newEntry.checklists)) {
        checklists.value = [...newEntry.checklists]
      }
      if (Array.isArray(newEntry.observers)) {
        taskObservers.value = [...newEntry.observers]
      }
      if (Array.isArray(newEntry.subtasks)) {
        subtasksList.value = [...newEntry.subtasks]
      }
    }
  },
  { deep: true },
)

function handleRealtimeEvent(e: Event) {
  const customEvent = e as CustomEvent<IRealtimePayload & { fullDetails?: any }>
  const payload = customEvent.detail
  if (!payload?.data) return
  const entry = payload.data as IResponsibilityListItem & { id?: number }
  if (!entry?.id) return

  // 1. Если обновилась сама текущая открытая задача (чек-листы, файлы, статус, подзадачи)
  if (entry.id === taskData.value.id) {
    const full = payload.fullDetails
    const merged = full ? { ...taskData.value, ...full } : { ...taskData.value, ...entry }
    taskData.value = merged

    // Синхронизируем чек-листы с сервера
    if (full && Array.isArray(full.checklists)) {
      checklists.value = [...full.checklists]
    } else if (Array.isArray(entry.checklists)) {
      checklists.value = [...entry.checklists]
    }

    // Синхронизируем наблюдателей с сервера
    if (full && Array.isArray(full.observers)) {
      taskObservers.value = [...full.observers]
    } else if (Array.isArray(entry.observers)) {
      taskObservers.value = [...entry.observers]
    }

    // Синхронизируем подзадачи
    if (Array.isArray(merged.subtasks)) {
      subtasksList.value = [...merged.subtasks]
    }

    // Синхронизируем файлы задачи, если они пришли
    if (Array.isArray(merged.files)) {
      taskData.value.files = merged.files
    }

    // Синхронизируем заголовок, если не в режиме редактирования
    if (!isEditingTitle.value && (merged.title || merged.instructions)) {
      titleInput.value = merged.title || merged.instructions || ''
    }
    return
  }

  // 2. Если обновилась одна из подзадач текущей задачи
  const parentId = typeof entry.parent_entry === 'object' && entry.parent_entry ? entry.parent_entry.id : Number(entry.parent_entry)
  const isMySubtask = parentId === taskData.value.id || subtasksList.value.some(s => s.id === entry.id)
  if (isMySubtask) {
    const idx = subtasksList.value.findIndex(s => s.id === entry.id)
    if (idx >= 0) {
      if (payload.event_type === 'responsibility_entry_deleted') {
        subtasksList.value.splice(idx, 1)
      } else {
        subtasksList.value.splice(idx, 1, {
          ...subtasksList.value[idx],
          ...entry,
          status: entry.status,
          status_label: entry.status_label,
        })
      }
    } else if (payload.event_type === 'responsibility_entry_created') {
      subtasksList.value.unshift(entry)
    }
    subtasksList.value = [...subtasksList.value]
  }
}

onMounted(() => {
  loadTaskDetails()
  window.addEventListener('responsibility-entry-realtime', handleRealtimeEvent)
})

onUnmounted(() => {
  window.removeEventListener('responsibility-entry-realtime', handleRealtimeEvent)
})

async function loadTaskDetails() {
  const entryId = taskData.value.id
  if (!entryId) return

  titleInput.value = taskData.value.title || taskData.value.instructions || ''

  if (props.isDemo) {
    checklists.value = [...(taskData.value.checklists ?? [])]
    taskObservers.value = [...(taskData.value.observers ?? [])]
    subtasksList.value = [...(taskData.value.subtasks ?? [])]

    return
  }

  // Догружаем полные данные (actions, editable_fields, checklists, observers), если их нет
  if (
    !taskData.value.actions
    || taskData.value.actions.length === 0
    || !Array.isArray(taskData.value.checklists)
    || !Array.isArray(taskData.value.observers)
  ) {
    const fullDetails = await tasksStore.fetchEntryById(entryId)
    if (fullDetails) {
      taskData.value = { ...taskData.value, ...fullDetails }
      if (!isEditingTitle.value) {
        titleInput.value = taskData.value.title || taskData.value.instructions || ''
      }
    }
  }

  // Если чек-листы уже пришли в объекте задачи с бэкенда — используем их напрямую
  if (Array.isArray(taskData.value.checklists)) {
    checklists.value = [...taskData.value.checklists]
  } else {
    checklists.value = await tasksStore.fetchChecklists(entryId)
  }

  // Если наблюдатели уже пришли в объекте задачи с бэкенда — используем их напрямую
  if (Array.isArray(taskData.value.observers)) {
    taskObservers.value = [...taskData.value.observers]
  } else {
    taskObservers.value = await tasksStore.fetchObservers(entryId)
  }

  // Подзадачи (если родительская задача)
  if (!hasParentTask.value) {
    if (Array.isArray(taskData.value.subtasks)) {
      subtasksList.value = [...taskData.value.subtasks]
    }
  }
}

const fallbackAvatars = new Map<string | number, string>()

function getEmployeeAvatar(emp?: any, fallbackKey?: string | number): string {
  if (emp?.avatar) return emp.avatar
  const id = emp?.id || fallbackKey || 'unknown'
  if (!fallbackAvatars.has(id)) {
    fallbackAvatars.set(id, getRandomAvatar())
  }
  return fallbackAvatars.get(id)!
}

function startEditTitle() {
  if (!canEditTitle.value) return
  isEditingTitle.value = true
  titleInput.value = taskData.value.title || taskData.value.instructions || ''
}

async function saveTitle() {
  if (isSavingTitle.value) return
  const newTitle = titleInput.value.trim()
  isEditingTitle.value = false
  if (!newTitle || newTitle === taskData.value.title) return

  const previousTitle = taskData.value.title
  // Оптимистичное обновление: сразу отображаем новый заголовок, чтобы не мигал старый
  taskData.value.title = newTitle
  isSavingTitle.value = true

  try {
    const updated = await tasksStore.updateTaskDetails(taskData.value.id!, { title: newTitle })
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      emit('updated', taskData.value)
    } else {
      // Откат при ошибке сети
      taskData.value.title = previousTitle
      titleInput.value = previousTitle || ''
    }
  } finally {
    isSavingTitle.value = false
  }
}

async function saveDescription(newDesc: string) {
  if (newDesc === taskData.value.instructions) return
  if (!taskData.value.id) return

  const previousDesc = taskData.value.instructions
  // Оптимистичное обновление описания
  taskData.value.instructions = newDesc

  const updated = await tasksStore.updateTaskDetails(taskData.value.id, { instructions: newDesc })
  if (updated) {
    taskData.value = { ...taskData.value, ...updated }
    emit('updated', taskData.value)
  } else {
    // Откат при ошибке сети
    taskData.value.instructions = previousDesc
  }
}

async function togglePin() {
  if (!taskData.value.id) return
  const isPinned = Boolean(taskData.value.is_pinned)
  const result = await tasksStore.togglePin(taskData.value.id, isPinned)
  taskData.value.is_pinned = result
  if (result !== isPinned) {
    emit('updated', taskData.value)
  }
}

async function navigateToParentTask() {
  const p = parentEntryData.value
  const id = typeof p === 'object' && p ? p.id : Number(p)
  if (!id) return

  // 1. Проверяем детали родительской задачи
  let parent = parentEntryDetail.value
  if (!parent && id) {
    try {
      parent = await tasksStore.fetchEntryById(id)
    } catch {
      // Игнорируем сетевые ошибки
    }
  }

  const urlProps = {
    ...(taskData.value.url_props || {}),
    ...(parent?.url_props || {}),
  }

  // Фоллбэк на ID текущего события/тикета/предложения, если координаторская задача открыта в контексте сущности
  if (!urlProps.eventId && (parent?.source_type === 'adverseevent' || taskData.value.source_type === 'adverseevent') && apiStore.currentAdverseEvent?.id) {
    urlProps.eventId = apiStore.currentAdverseEvent.id
  }
  if (!urlProps.ticketId && (parent?.source_type === 'ticket' || taskData.value.source_type === 'ticket') && ticketsStore.currentTicket?.id) {
    urlProps.ticketId = Number(ticketsStore.currentTicket.id)
  }
  if (!urlProps.suggestionId && (parent?.source_type === 'improvementsuggestion' || taskData.value.source_type === 'improvementsuggestion') && improvementsStore.currentSuggestion?.id) {
    urlProps.suggestionId = Number(improvementsStore.currentSuggestion.id)
  }
  if (!urlProps.appealId && (parent?.source_type === 'appeal' || taskData.value.source_type === 'appeal') && citizenInquiriesStore.currentAppeal?.id) {
    urlProps.appealId = citizenInquiriesStore.currentAppeal.id
  }

  /*
   * 2. Если родительская задача привязана к сущности — открываем форму сущности через системную утилиту.
   * entryId исключаем: это сама задача (поручение), её открывает шаг 3, а ветка ниже
   * обнуляет globalResponsibilityEntryId и закрыла бы модалку задачи.
   */
  const opened = openEntityByUrlProps({ ...urlProps, entryId: undefined }, true)
  if (opened) {
    feedbackStore.globalResponsibilityEntryId = null
    if (urlProps.eventId) {
      void apiStore.fetchAdverseEventById({ id: Number(urlProps.eventId) })
    } else if (urlProps.ticketId) {
      void ticketsStore.fetchTicketById(Number(urlProps.ticketId))
    } else if (urlProps.suggestionId) {
      void improvementsStore.fetchSuggestionById(Number(urlProps.suggestionId))
    } else if (urlProps.appealId) {
      void citizenInquiriesStore.fetchAppealById(String(urlProps.appealId))
    }
    return
  }

  // 3. Если основания нет — открываем как самостоятельную задачу
  emit('navigate-task', id)
  feedbackStore.globalResponsibilityEntryId = id
}

function handleOpenSubtask(st: IResponsibilityListItem) {
  if (st.id) {
    emit('navigate-task', st.id)
    feedbackStore.globalResponsibilityEntryId = st.id
  }
}

function handleSubtaskCreated(created: IResponsibilityListItem | IResponsibilityListItem[]) {
  const items = Array.isArray(created) ? created : [created]
  subtasksList.value.unshift(...items)

  const eventId = Number(taskData.value.url_props?.eventId || apiStore.currentAdverseEvent?.id)
  if (eventId && (taskData.value.source_type === 'adverseevent' || taskData.value.is_coordinator_entry)) {
    void apiStore.fetchAdverseEventById({ id: eventId })
  }
  const ticketId = Number(taskData.value.url_props?.ticketId || ticketsStore.currentTicket?.id)
  if (ticketId && (taskData.value.source_type === 'ticket' || taskData.value.is_coordinator_entry)) {
    void ticketsStore.fetchTicketById(ticketId)
  }
  const suggestionId = Number(taskData.value.url_props?.suggestionId || improvementsStore.currentSuggestion?.id)
  if (suggestionId && (taskData.value.source_type === 'improvementsuggestion' || taskData.value.is_coordinator_entry)) {
    void improvementsStore.fetchSuggestionById(suggestionId)
  }
  const appealId = taskData.value.url_props?.appealId || citizenInquiriesStore.currentAppeal?.id
  if (appealId && (taskData.value.source_type === 'appeal' || taskData.value.is_coordinator_entry)) {
    void citizenInquiriesStore.fetchAppealById(String(appealId))
  }
}

async function addChecklist() {
  if (!taskData.value.id) return
  const cl = await tasksStore.createChecklist(taskData.value.id, 'Чек-лист')
  if (cl) {
    newlyCreatedChecklistIds.value.add(cl.id)
    checklists.value.push(cl)
  }
}

function handleChecklistDeleted(checklistId: number) {
  checklists.value = checklists.value.filter(c => c.id !== checklistId)
}

function handleObserversUpdated(updated: ITaskObserver[]) {
  taskObservers.value = updated
}

const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

function openChangePerformerModal() {
  if (!canChangePerformer.value) return
  if (!apiStore.employees || apiStore.employees.length === 0) {
    apiStore.fetchAllEmployees()
  }
  employeeModalRef.value?.open()
}

async function handlePerformerSelected(emp: IEmployeeTreeItem) {
  if (!taskData.value.id || !emp?.id) return

  const { first_name, last_name, middle_name } = parseFullName(emp.full_name || '')
  const newResponsibleEmployee = {
    id: emp.id,
    full_name: emp.full_name,
    first_name,
    last_name,
    middle_name,
    position_name: emp.position_name,
  }

  // 1. Координаторская запись сущности (НС, Заявка или ППУ) — через change-coordinator сущности
  if (taskData.value.is_coordinator_entry) {
    const eventId = Number(taskData.value.url_props?.eventId || apiStore.currentAdverseEvent?.id)
    const ticketId = Number(taskData.value.url_props?.ticketId || ticketsStore.currentTicket?.id)
    const suggestionId = Number(taskData.value.url_props?.suggestionId || improvementsStore.currentSuggestion?.id)
    const appealId = taskData.value.url_props?.appealId || citizenInquiriesStore.currentAppeal?.local_id || citizenInquiriesStore.currentAppeal?.id

    /*
     * Обращение проверяем первым и строго по source_type: ветки НС/Заявки ниже срабатывают и по
     * «залежавшемуся» currentAdverseEvent/currentTicket в сторе и перехватили бы смену координатора обращения.
     */
    if (appealId && taskData.value.source_type === 'appeal') {
      const updatedEntry = await citizenInquiriesStore.changeAppealCoordinator({
        appeal_id: appealId,
        coordinator_id: emp.id,
      })
      if (updatedEntry) {
        taskData.value = {
          ...taskData.value,
          ...updatedEntry,
          responsible_employee: (updatedEntry.responsible_employee as any) || newResponsibleEmployee,
        }
        emit('updated', taskData.value)
      }
      return
    } else if (eventId && (taskData.value.source_type === 'adverseevent' || apiStore.currentAdverseEvent?.id === eventId)) {
      const updatedEntry = await apiStore.changeAdverseEventCoordinator({
        event_id: eventId,
        coordinator_id: emp.id,
      })
      if (updatedEntry) {
        taskData.value = {
          ...taskData.value,
          ...updatedEntry,
          responsible_employee: (updatedEntry.responsible_employee as any) || newResponsibleEmployee,
        }
        emit('updated', taskData.value)
        return
      }
    } else if (ticketId && (taskData.value.source_type === 'ticket' || Number(ticketsStore.currentTicket?.id) === ticketId)) {
      const updatedEntry = await ticketsStore.changeTicketCoordinator({
        event_id: ticketId,
        coordinator_id: emp.id,
      })
      if (updatedEntry) {
        taskData.value = {
          ...taskData.value,
          ...updatedEntry,
          responsible_employee: (updatedEntry.responsible_employee as any) || newResponsibleEmployee,
        }
        emit('updated', taskData.value)
        return
      }
    } else if (suggestionId && taskData.value.source_type === 'improvementsuggestion') {
      const updatedEntry = await improvementsStore.changeSuggestionCoordinator({
        suggestion_id: suggestionId,
        coordinator_id: emp.id,
      })
      if (updatedEntry) {
        taskData.value = {
          ...taskData.value,
          ...updatedEntry,
          responsible_employee: (updatedEntry.responsible_employee as any) || newResponsibleEmployee,
        }
        emit('updated', taskData.value)
      }
      return
    }
  }

  // 2. Обычная задача
  const updated = await tasksStore.updateTaskDetails(taskData.value.id, {
    responsible_employee_id: emp.id,
    responsible_employee_ids: [emp.id],
  })
  if (updated) {
    taskData.value = {
      ...taskData.value,
      ...updated,
      responsible_employee: (updated.responsible_employee as any) || newResponsibleEmployee,
    }
    emit('updated', taskData.value)
  }
}

async function takeInProgress() {
  if (!taskData.value.id) return
  isActionLoading.value = true
  try {
    const updated = await tasksStore.takeInProgress(taskData.value.id)
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      emit('updated', taskData.value)
    }
  } finally {
    isActionLoading.value = false
  }
}

async function returnInProgress() {
  if (!taskData.value.id) return
  isActionLoading.value = true
  try {
    const updated = await tasksStore.returnInProgress(taskData.value.id)
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      emit('updated', taskData.value)
    }
  } finally {
    isActionLoading.value = false
  }
}

function triggerTaskFileInput() {
  taskFileAttachRef.value?.openDialog()
}

async function handleNewFilesUploaded(files: File[]) {
  taskNewFiles.value = files
  if (files.length > 0 && taskData.value.id) {
    const updated = await tasksStore.uploadTaskFiles(taskData.value.id, files)
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      taskNewFiles.value = []
      emit('updated', taskData.value)
    }
  }
}

const isReportRequired = computed(() => {
  return taskData.value.is_report_required !== false
})

const completedReportText = computed(() => {
  if (taskData.value.last_report) return taskData.value.last_report
  if (taskData.value.report) return taskData.value.report
  const reports = taskData.value.reports
  if (Array.isArray(reports) && reports.length > 0) {
    const lastItem = reports[reports.length - 1]
    return lastItem?.report || ''
  }
  return ''
})

const completedReportFiles = computed<IExistingFile[]>(() => {
  return (taskData.value.files || []) as IExistingFile[]
})

// Проверка невыполненных пунктов чек-листа перед завершением
const hasUncompletedChecklistItems = computed(() => {
  if (!checklists.value || checklists.value.length === 0) return false
  return checklists.value.some(cl => (cl.items || []).some(item => !item.is_completed))
})

// Проверка незавершенных подзадач перед завершением
const hasUncompletedSubtasks = computed(() => {
  if (!subtasksList.value || subtasksList.value.length === 0) return false
  return subtasksList.value.some(st => st.status !== 'completed' && st.status !== 'cancelled')
})

const hasIncompleteDependencies = computed(() => {
  return hasUncompletedChecklistItems.value || hasUncompletedSubtasks.value
})

const incompleteDialogText = computed(() => {
  if (hasUncompletedChecklistItems.value && hasUncompletedSubtasks.value) {
    return 'В задаче есть незавершённые подзадачи и невыполненные пункты чек-листа. Вы уверены, что хотите завершить задачу?'
  }
  if (hasUncompletedSubtasks.value) {
    return 'У задачи есть незавершённые подзадачи. Вы уверены, что хотите завершить задачу?'
  }
  return 'В чек-листе остались невыполненные пункты. Вы уверены, что хотите завершить задачу?'
})

const showChecklistError = ref(false)
const showIncompleteItemsDialog = ref(false)

watch(hasUncompletedChecklistItems, hasUncompleted => {
  if (!hasUncompleted) {
    showChecklistError.value = false
  }
})

function handleCompleteActionClick() {
  if (hasIncompleteDependencies.value) {
    showChecklistError.value = hasUncompletedChecklistItems.value
    showIncompleteItemsDialog.value = true
    return
  }

  showChecklistError.value = false
  if (isReportRequired.value) {
    isWritingReport.value = true
  } else {
    void executeSubmitComplete()
  }
}

function handleIncompleteItemsConfirm(confirmed: boolean) {
  showIncompleteItemsDialog.value = false
  if (confirmed) {
    if (isWritingReport.value) {
      void executeSubmitComplete()
    } else if (isReportRequired.value) {
      isWritingReport.value = true
    } else {
      void executeSubmitComplete()
    }
  }
}

const showUnsavedReportDialog = ref(false)

function cancelWritingReport() {
  if (completeReportText.value.trim() || completeFiles.value.length > 0) {
    showUnsavedReportDialog.value = true
  } else {
    resetReportDirectly()
  }
}

function handleCancelReportConfirm(confirmed: boolean) {
  showUnsavedReportDialog.value = false
  if (confirmed) {
    resetReportDirectly()
  }
}

function resetReportDirectly() {
  isWritingReport.value = false
  completeReportText.value = ''
  completeFiles.value = []
}

async function submitComplete() {
  if (hasIncompleteDependencies.value) {
    showChecklistError.value = hasUncompletedChecklistItems.value
    showIncompleteItemsDialog.value = true
    return
  }

  showChecklistError.value = false
  await executeSubmitComplete()
}

async function executeSubmitComplete() {
  if (!taskData.value.id) return
  isActionLoading.value = true
  try {
    const updated = await tasksStore.completeTask(
      taskData.value.id,
      completeReportText.value.trim(),
      completeFiles.value.length > 0 ? completeFiles.value : undefined,
    )
    if (updated) {
      taskData.value = { ...taskData.value, ...updated }
      showChecklistError.value = false
      isWritingReport.value = false
      completeReportText.value = ''
      completeFiles.value = []
      emit('updated', taskData.value)

      // Актуализируем данные сущности-основания (НС / Заявка / ППУ), если подзадача завершена в контексте сущности
      const eventId = Number(taskData.value.url_props?.eventId || apiStore.currentAdverseEvent?.id)
      if (eventId && (taskData.value.source_type === 'adverseevent' || hasParentTask.value)) {
        void apiStore.fetchAdverseEventById({ id: eventId })
      }
      const ticketId = Number(taskData.value.url_props?.ticketId || ticketsStore.currentTicket?.id)
      if (ticketId && (taskData.value.source_type === 'ticket' || hasParentTask.value)) {
        void ticketsStore.fetchTicketById(ticketId)
      }
      const suggestionId = Number(taskData.value.url_props?.suggestionId || improvementsStore.currentSuggestion?.id)
      if (suggestionId && (taskData.value.source_type === 'improvementsuggestion' || hasParentTask.value)) {
        void improvementsStore.fetchSuggestionById(suggestionId)
      }
      const appealId = taskData.value.url_props?.appealId || citizenInquiriesStore.currentAppeal?.id
      if (appealId && (taskData.value.source_type === 'appeal' || hasParentTask.value)) {
        void citizenInquiriesStore.fetchAppealById(String(appealId))
      }
    }
  } finally {
    isActionLoading.value = false
  }
}

async function handleDeleteConfirm(confirmed: boolean) {
  if (confirmed && taskData.value.id) {
    const success = await tasksStore.deleteEntry(taskData.value)
    if (success) {
      emit('deleted', taskData.value.id)
    }
  }
  isDeleteDialogOpen.value = false
}

function copyLink() {
  if (!taskData.value.id) return
  const url = `${window.location.origin}/task?task_id=${taskData.value.id}`
  navigator.clipboard.writeText(url)
  feedbackStore.showToast({
    type: 'success',
    message: 'Ссылка на задачу скопирована',
  })
}

function copyTaskId() {
  if (!taskData.value.id) return
  navigator.clipboard.writeText(String(taskData.value.id))
  feedbackStore.showToast({
    type: 'success',
    message: `ID ${taskData.value.id} скопирован`,
  })
}

async function togglePriority() {
  if (!taskData.value.id) return
  const success = await tasksStore.togglePriority(taskData.value)
  if (success !== undefined) {
    emit('updated', taskData.value)
  }
}

const observersModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

function openAddObserversModal() {
  observersModalRef.value?.open()
}

async function handleObserversSelected(employees: IEmployeeTreeItem[]) {
  if (!taskData.value.id || !employees || employees.length === 0) return
  await tasksStore.addObservers(taskData.value.id, employees.map(emp => emp.id))
  taskObservers.value = await tasksStore.fetchObservers(taskData.value.id)
}
</script>

<style scoped lang="scss">
.task-card-root {
  min-height: 480px;
}

.auto-created-text {
  color: rgba(var(--v-theme-text-primary), var(--v-disabled-opacity));
}

// xs: иконки шапки первой строкой над заголовком, равномерно — как панель кнопок в форме НС
.task-card-header-actions--xs {
  order: -1;
  justify-content: space-evenly;
  inline-size: 100%;
  margin-block-end: 8px;
}

.metadata-label {
  width: 140px;
  flex-shrink: 0;
  white-space: pre-line;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: rgb(var(--v-theme-secondary));
}

.metadata-value {
  flex-grow: 1;
}

.hover-editable-text {
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}

.hover-editable-text:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.hover-bg-subtle:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.task-card-title {
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
}

.task-title-inline-input :deep(input) {
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  padding: 0;
}

:deep(.task-burger-menu) {
  box-shadow: 0px 6px 16px rgba(46, 38, 61, 0.22) !important;
  border-radius: 6px !important;

  .task-burger-menu-item {
    min-height: 36px !important;
    padding: 6px 14px !important;
  }

  .task-burger-menu-text {
    font-family: Inter, sans-serif !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    white-space: nowrap;
  }

  .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
  }
}

.task-section-header {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
}
</style>

<style lang="scss">
.task-burger-menu {
  box-shadow: 0px 6px 16px rgba(46, 38, 61, 0.22) !important;
  border-radius: 6px !important;

  .task-burger-menu-item {
    min-height: 36px !important;
    padding: 6px 14px !important;
  }

  .task-burger-menu-text {
    font-family: Inter, sans-serif !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    white-space: nowrap;
  }

  .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
  }
}
</style>
